import React from 'react';

import { Box } from '@rbilabs/universal-components';

import BlockRenderer from 'components/sanity-block-renderer';
import { Typography, TypographyProps } from 'components/ui';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { getDesignToken } from 'styles/constants';

const staticPageHeader2: Record<string, Partial<TypographyProps>> = {
  'terms-conditions-rewards': {
    textAlign: 'left',
  },
};

const staticPageHeader3: Record<string, Partial<TypographyProps>> = {
  'about-bk': {
    textAlign: 'left',
    fontFamily: 'body',
  },
  'terms-of-service': {
    textAlign: 'left',
    fontFamily: 'body',
  },
};

const headerCommonStyle = { fontWeight: '300' as const, display: 'flex' as const };

const headerCommonSpacing = {
  p: '$0' as const,
  pt: '$8' as const,
  m: '$0' as const,
  mb: '$2' as const,
};

type StaticPageHeaderProps = TypographyProps & { pageSlug?: string };

export const UnknownWidget = Box;

// `fontSize` is overridden per breakpoint below, but `Typography` only derives a matching
// `lineHeight` from the `variant`'s own (smaller, mobile) font size when no `lineHeight` is
// passed explicitly — leaving the line height too tight for the larger overridden size, and
// causing wrapped header text (e.g. in the narrower sign-up terms dialog) to visually overlap
// between lines. Passing `lineHeightMultiplier` here recomputes it against the actual
// `fontSize` in use, at the same ~1.25x ratio the design tokens use for other headers.
const HEADER_LINE_HEIGHT_MULTIPLIER = 1.25;

export const Header1 = (props: StaticPageHeaderProps) => {
  const isDesktop = useIsDesktopBreakpoint();

  return (
    <Typography
      variant="headerOne"
      textAlign="center"
      fontSize={isDesktop ? '6xl' : '4xl'}
      lineHeightMultiplier={HEADER_LINE_HEIGHT_MULTIPLIER}
      style={headerCommonStyle}
      {...headerCommonSpacing}
      {...props}
    />
  );
};

export const Header2 = ({ pageSlug, ...props }: StaticPageHeaderProps & { pageSlug: string }) => {
  const isDesktop = useIsDesktopBreakpoint();

  return (
    <Typography
      variant="headerTwo"
      textAlign="center"
      fontSize={isDesktop ? '4xl' : '2xl'}
      lineHeightMultiplier={HEADER_LINE_HEIGHT_MULTIPLIER}
      style={headerCommonStyle}
      {...headerCommonSpacing}
      {...(staticPageHeader2[pageSlug] ?? {})}
      {...props}
    />
  );
};

export const Header3 = ({ pageSlug, ...props }: StaticPageHeaderProps & { pageSlug: string }) => {
  const isDesktop = useIsDesktopBreakpoint();

  return (
    <Typography
      variant="headerThree"
      textAlign="center"
      fontSize={isDesktop ? '3xl' : 'lg'}
      lineHeightMultiplier={HEADER_LINE_HEIGHT_MULTIPLIER}
      style={headerCommonStyle}
      {...headerCommonSpacing}
      {...(staticPageHeader3[pageSlug] ?? {})}
      {...props}
    />
  );
};

export const Header4 = (props: StaticPageHeaderProps) => (
  <Typography
    variant="headerFour"
    fontSize="lg"
    lineHeightMultiplier={HEADER_LINE_HEIGHT_MULTIPLIER}
    style={headerCommonStyle}
    {...headerCommonSpacing}
    {...props}
  />
);

export const Header5 = (props: StaticPageHeaderProps) => (
  <Typography
    variant="headerFour"
    fontSize="lg"
    lineHeightMultiplier={HEADER_LINE_HEIGHT_MULTIPLIER}
    style={headerCommonStyle}
    {...headerCommonSpacing}
    {...props}
  />
);

const staticPageParagraph: Record<string, React.ComponentPropsWithoutRef<typeof Typography>> = {
  'about-bk': {
    mt: '$1',
  },
};

export const Paragraph = ({
  pageSlug,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & { pageSlug: string }) => {
  const isDesktop = useIsDesktopBreakpoint();
  return React.createElement(Typography, {
    variant: 'copyOne',
    fontSize: isDesktop ? 'xl' : 'md',
    lineHeight: 'lg',
    mt: '$8',
    ...(staticPageParagraph[pageSlug] ?? {}),
    ...props,
  });
};

export const BlockWidgetContainer = BlockRenderer.withConfig({
  marginBottom: '$6',
  marginX: 'auto',
  width: 'full',
});

export const ListItem = (props: React.ComponentPropsWithoutRef<typeof Typography>) => {
  const isDesktop = useIsDesktopBreakpoint();
  return React.createElement(Typography, {
    variant: 'copyOne',
    fontSize: isDesktop ? 'xl' : 'md',
    lineHeight: 'md',
    mt: '$2',
    ...props,
  });
};

export const blockWidgetStrongProps = {
  lineHeight: 'lg',
};

export const blockWidgetLinkProps = {
  color: getDesignToken('text-link-hover'),
};
